import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Clock, BookOpen, Users, Star, ArrowRight, Filter, GraduationCap } from 'lucide-react';
import { getPathway } from '../utils/pathways';

const tracks = [
  { id: '', label: 'All Pathways' },
  { id: 'computer-science', label: 'Computer Science' },
  { id: 'data-science', label: 'Data Science' },
  { id: 'ai-ml', label: 'AI & Machine Learning' },
  { id: 'cybersecurity', label: 'Cybersecurity' },
  { id: 'software-engineering', label: 'Software Engineering' },
  { id: 'mobile-dev', label: 'Mobile Development' },
  { id: 'cloud', label: 'Cloud & DevOps' },
  { id: 'game-dev', label: 'Game Development' },
];

const MOCK_COURSES = [
  { 
    _id: '1', 
    title: 'Computer Science Pathway', 
    track: 'computer-science', 
    level: 'beginner', 
    price: 0, 
    duration: 180, 
    totalLessons: 52, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Master the fundamentals of computer science — algorithms, data structures, programming concepts and problem solving. Perfect starting point for any IT career.', 
  },
  { 
    _id: '2', 
    title: 'Data Science Pathway', 
    track: 'data-science', 
    level: 'beginner', 
    price: 0, 
    duration: 160, 
    totalLessons: 44, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Learn data analysis, statistics, Python, visualization and machine learning basics. Turn raw data into powerful insights.', 
  },
  { 
    _id: '3', 
    title: 'AI & Machine Learning Pathway', 
    track: 'ai-ml', 
    level: 'intermediate', 
    price: 0, 
    duration: 150, 
    totalLessons: 40, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Explore artificial intelligence, neural networks, deep learning and real-world AI applications using Python and modern frameworks.', 
  },
  { 
    _id: '4', 
    title: 'Cybersecurity Pathway', 
    track: 'cybersecurity', 
    level: 'beginner', 
    price: 0, 
    duration: 140, 
    totalLessons: 38, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Understand ethical hacking, network security, cryptography and cyber defence. Protect systems and launch a career in cybersecurity.', 
  },
  { 
    _id: '5', 
    title: 'Full Stack Web Development', 
    track: 'software-engineering', 
    level: 'beginner', 
    price: 0, 
    duration: 200, 
    totalLessons: 60, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Build complete web applications from scratch. HTML, CSS, JavaScript, React, Node.js, databases and deployment — all in one pathway.', 
  },
  { 
    _id: '6', 
    title: 'Mobile App Development', 
    track: 'mobile-dev', 
    level: 'beginner', 
    price: 0, 
    duration: 130, 
    totalLessons: 36, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Create Android and iOS apps using React Native. From UI design to app store publishing — learn mobile development end to end.', 
  },
  { 
    _id: '7', 
    title: 'Cloud & DevOps Pathway', 
    track: 'cloud', 
    level: 'intermediate', 
    price: 0, 
    duration: 120, 
    totalLessons: 32, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Master AWS, Docker, Kubernetes, CI/CD pipelines and cloud architecture. Essential skills for modern software deployment.', 
  },
  { 
    _id: '8', 
    title: 'Game Development Pathway', 
    track: 'game-dev', 
    level: 'beginner', 
    price: 0, 
    duration: 140, 
    totalLessons: 38, 
    enrollmentCount: 0, 
    rating: 5.0, 
    thumbnail: null, 
    shortDescription: 'Design and build 2D and 3D games using Unity and C#. Learn game mechanics, physics, animation and publishing.', 
  },
];

function CourseCard({ course }) {
  const [hovered, setHovered] = useState(false);
  const tc = getPathway(course.track);

  return (
    <div className="card-edu"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
      {/* Thumb */}
      <div style={{
        height: 140, borderRadius: 'var(--radius-md)', marginBottom: '1rem',
        background: `linear-gradient(135deg, ${tc.color}18, ${tc.color}08)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${tc.color}20`,
        transition: 'transform 0.3s',
        transform: hovered ? 'scale(1.02)' : 'scale(1)',
      }}>
        <tc.Icon size={46} color={tc.color} strokeWidth={1.5} />
      </div>
      <div className={`badge ${tc.badge}`} style={{ marginBottom: 8, alignSelf: 'flex-start' }}>
        {tracks.find(t => t.id === course.track)?.label || course.track}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 6, lineHeight: 1.3 }}>{course.title}</h3>
      <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.6, flex: 1, marginBottom: '1rem' }}>{course.shortDescription}</p>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={12} /> {Math.round(course.duration / 60)}h total
        </span>
        <span style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <BookOpen size={12} /> {course.totalLessons} lessons
        </span>
        <span style={{ fontSize: 12, color: 'var(--ac-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Users size={12} /> {course.enrollmentCount} enrolled
        </span>
        <span style={{ fontSize: 12, color: 'var(--ac-warn)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Star size={12} fill="var(--ac-warn)" /> {course.rating}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--ac-white)' }}>${course.price}</span>
          {course.originalPrice && <span style={{ fontSize: 13, color: 'var(--ac-muted)', textDecoration: 'line-through', marginLeft: 8 }}>${course.originalPrice}</span>}
        </div>
        {/* Enrolments are closed — the enrol action is disabled, details stay browsable */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="btn btn-sm btn-disabled" aria-disabled="true">Enrolment Closed</span>
          <Link to={`/educenter/${course._id}`} className="btn btn-sm btn-ghost" style={{ gap: 6 }}>
            Details <ArrowRight size={14} style={{ transform: hovered ? 'translateX(4px)' : 'none', transition: 'transform 0.2s' }} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function EduCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState(MOCK_COURSES);
  const [search, setSearch] = useState('');
  const activeTrack = searchParams.get('track') || '';

  const filtered = courses.filter(c => {
    const matchTrack = !activeTrack || c.track === activeTrack;
    const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase());
    return matchTrack && matchSearch;
  });

  return (
    <div style={{ paddingTop: 68 }}>
      {/* Hero */}
      <section style={{
        padding: '4.5rem 0 3.5rem', textAlign: 'center',
        background: `
          radial-gradient(ellipse at 30% 50%, rgba(168,85,247,0.08) 0%, transparent 55%),
          radial-gradient(ellipse at 70% 30%, rgba(0,200,255,0.06) 0%, transparent 55%),
          var(--ac-dark)`,
      }}>
        <div className="container">
          <div className="badge badge-purple" style={{ marginBottom: '1.2rem' }}><GraduationCap size={14} /> AlteraCode EduCenter</div>
          <h1 style={{ fontSize: 'clamp(30px, 5vw, 56px)', fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.15, marginBottom: '1rem' }}>
            <span className="grad-text-edu">Altera Scholar</span><br />Your IT Pathway Starts Here
          </h1>
          <p style={{ fontSize: 16, color: 'var(--ac-muted)', maxWidth: 600, margin: '0 auto 2rem', lineHeight: 1.8 }}>
            Altera Scholar is a comprehensive video series program built for school leavers globally.
            We cover every major IT pathway to help you bridge the gap between your A/L results and
            a successful technology career — before you even enter university.
          </p>

          {/* Enrolment status */}
          <div role="status" style={{ marginBottom: '1rem' }}>
            <span className="status-closed">
              <span className="status-closed__dot" />
              Enrollments are currently closed.
            </span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--ac-muted)', maxWidth: 520, margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
            You can still browse every pathway below. Registration will reopen ahead of the July 2026 launch —{' '}
            <Link to="/contact" style={{ color: 'var(--ac-cyan)', textDecoration: 'none', fontWeight: 600 }}>contact us</Link>{' '}
            to be notified.
          </p>

          {/* Search */}
          <div style={{ maxWidth: 520, margin: '0 auto', position: 'relative' }}>
            <Search size={18} color="var(--ac-muted)" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              className="form-input"
              placeholder="Search courses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 44, fontSize: 15, height: 50, borderRadius: 'var(--radius-xl)' }}
            />
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      <section className="section">
        <div className="container">
          {/* Track filter */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '2.5rem', alignItems: 'center' }}>
            <Filter size={16} color="var(--ac-muted)" />
            {tracks.map(t => (
              <button key={t.id} onClick={() => setSearchParams(t.id ? { track: t.id } : {})} style={{
                padding: '7px 18px', borderRadius: 20,
                background: activeTrack === t.id ? 'var(--grad-edu)' : 'var(--ac-card)',
                color: activeTrack === t.id ? 'white' : 'var(--ac-muted)',
                border: activeTrack === t.id ? 'none' : '1px solid var(--ac-border)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'var(--transition)',
              }}
                onMouseEnter={e => { if (activeTrack !== t.id) e.currentTarget.style.borderColor = 'rgba(168,85,247,0.4)'; }}
                onMouseLeave={e => { if (activeTrack !== t.id) e.currentTarget.style.borderColor = 'var(--ac-border)'; }}
              >{t.label}</button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ac-muted)' }}>{filtered.length} course{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--ac-muted)' }}>
              <BookOpen size={48} style={{ marginBottom: '1rem', opacity: 0.4 }} />
              <p>No courses found. Try a different search or filter.</p>
            </div>
          ) : (
            <div className="grid-3">
              {filtered.map(c => <CourseCard key={c._id} course={c} />)}
            </div>
          )}
        </div>
      </section>

      {/* Enrol CTA */}
      <section className="section-sm" style={{ background: 'var(--ac-navy)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '0.75rem' }}>Not sure which track to choose?</h2>
          <p style={{ color: 'var(--ac-muted)', marginBottom: '1.5rem', fontSize: 15 }}>Fill in our enquiry form and we'll help you pick the right path.</p>
          <Link to="/contact" className="btn btn-outline btn-lg">Talk to Our Team</Link>
        </div>
      </section>
    </div>
  );
}
